// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import MemoryIcon from '@mui/icons-material/Memory'
import TerminalIcon from '@mui/icons-material/Terminal'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import BulletGrid from '@/components/sections/BulletGrid'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

type Block = {
  icon: ReactElement
  title: string
  intro: string
  items: string[]
  note: string
}

export default function ModernizationSection() {
  const { t } = useTranslation('servicesTestEquipmentModernization')
  const { screenDimensions } = useContext(GlobalContext)

  const stacked = screenDimensions.width < breakpoints.desktopToMobile
  const iconSx = { fontSize: '1.9rem', color: themeColors.bg.start }

  const blocks: Block[] = [
    {
      icon: <MemoryIcon sx={iconSx} />,
      title: t('modernization.control.title'),
      intro: t('modernization.control.intro'),
      items: [
        t('modernization.control.items.01'),
        t('modernization.control.items.02'),
        t('modernization.control.items.03'),
        t('modernization.control.items.04'),
        t('modernization.control.items.05'),
        t('modernization.control.items.06'),
        t('modernization.control.items.07'),
        t('modernization.control.items.08'),
        t('modernization.control.items.09'),
        t('modernization.control.items.10')
      ],
      note: t('modernization.control.note')
    },
    {
      icon: <TerminalIcon sx={iconSx} />,
      title: t('modernization.software.title'),
      intro: t('modernization.software.intro'),
      items: [
        t('modernization.software.items.01'),
        t('modernization.software.items.02'),
        t('modernization.software.items.03'),
        t('modernization.software.items.04'),
        t('modernization.software.items.05'),
        t('modernization.software.items.06'),
        t('modernization.software.items.07'),
        t('modernization.software.items.08'),
        t('modernization.software.items.09')
      ],
      note: t('modernization.software.note')
    }
  ]

  const getBlock = (block: Block) => (
    <Box
      key={block.title}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: themeColors.kroemtech.background,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px',
        padding: '1.75rem',
        boxSizing: 'border-box'
      }}
    >
      <Box sx={{ lineHeight: 0, marginBottom: '1.1rem' }}>{block.icon}</Box>

      <Typography
        variant="h3"
        component="h3"
        sx={{
          fontSize: '1.05rem',
          fontWeight: 700,
          lineHeight: 1.3,
          color: themeColors.kroemtech.dunkelblau,
          marginBottom: '0.7rem'
        }}
      >
        {block.title}
      </Typography>

      <Typography
        sx={{
          fontSize: getFontBodySmall(),
          lineHeight: 1.65,
          color: themeColors.gray.defaultText,
          marginBottom: '1.25rem'
        }}
      >
        {block.intro}
      </Typography>

      <BulletGrid items={block.items} columns={2} />

      <Typography
        sx={{
          fontSize: getFontBodySmall(),
          lineHeight: 1.65,
          fontStyle: 'italic',
          color: themeColors.gray.defaultText,
          borderTop: `1px solid ${themeColors.gray.lighter}`,
          marginTop: '1.5rem',
          paddingTop: '1.25rem'
        }}
      >
        {block.note}
      </Typography>
    </Box>
  )

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={t('modernization.eyebrow')}
        title={t('modernization.title')}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: stacked ? '1fr' : 'repeat(2, 1fr)',
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {blocks.map(block => getBlock(block))}
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
